import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ElevatedButton, TextInput } from '@producer/ui-kit';

import { Project } from '../../../app/entities/project';
import { HasServerResponse } from '../../../components/PanelContainer';
import { Alert } from '../../../components/alert/Alert';
import { createFormDiff } from '../../../core/services/form.service';
import { Space } from '../../../app/entities/space';
import { FpsSelect } from '../../../features/projects/components/fps-select';
import { Tooltip } from '../../../components/tooltip/Tooltip';

import styles from './project-settings-form.module.scss';
interface ProjectSettingsFormProps {
  project?: Project;
}

interface FormValues {
  name: string;
  client: { name: string };
  agency: { name: string };
  fps?: number;
}

export const ProjectSettingsForm: React.FC<ProjectSettingsFormProps> = ({ project }) => {
  const { t } = useTranslation('createProject');
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<any>();

  const space = Space.getOne(project!.spaceId);
  const defaultFpsValue = project?.fps ?? space?.fps ?? 25;

  const schema = useMemo(
    () =>
      yup.object().shape({
        name: yup
          .string()
          .transform((value) => value.trim())
          .matches(/^(.*)?\S+(.*)?$/, t('common:fieldRequired'))
          .required(t('common:fieldRequired'))
          .max(32, t('common:fieldMax', { max: 32 })),
        client: yup.object().shape({
          name: yup.string().max(32, t('common:fieldMax', { max: 32 })),
        }),
        agency: yup.object().shape({
          name: yup
            .string()
            .max(32, t('common:fieldMax', { max: 32 }))
            .nullable(),
          fps: yup.number().positive(),
        }),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, dirtyFields },
  } = useForm<FormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    shouldUseNativeValidation: true,
    defaultValues: {
      name: project?.name,
      client: { ...project?.client, name: project?.client?.name || '' },
      agency: { ...project?.agency, name: project?.agency?.name || '' },
      fps: defaultFpsValue,
    },
  });

  const handleSubmitEdit = async (values: FormValues) => {
    if (!project) return;

    const diffs = createFormDiff<FormValues>(values, dirtyFields);

    if (values.fps !== undefined) {
      project.fps = values.fps;
    }

    await project.update(diffs);

    // Reset the form with value in place to clean dirty fields
    reset(undefined, {
      keepValues: true,
    });
  };

  const onHandleSubmit = async (values: any) => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      await handleSubmitEdit({
        ...values,
        fps: parseFloat(values.fps),
      });
    } catch (e) {
      setServerError(e);
    }

    setIsLoading(false);
  };

  return (
    <div className={styles['container']}>
      {serverError && serverError.response && (
        <HasServerResponse>
          <Alert text={serverError.response.data.message} />
        </HasServerResponse>
      )}

      <form className={styles['settings-form']}>
        <div className={styles['field-container']}>
          <TextInput
            {...register('name')}
            errorMessage={errors.name?.message}
            label={t('name')}
            placeholder={t('name_placeholder')}
            required
          />
        </div>

        <div className={styles['field-container']}>
          <TextInput
            {...register('client.name')}
            errorMessage={errors.client?.name?.message}
            label={t('client')}
            placeholder={t('client_placeholder')}
            autocomplete="false"
          />
        </div>

        <div className={styles['field-container']}>
          <TextInput
            {...register('agency.name')}
            errorMessage={errors.agency?.name?.message}
            label={t('agency')}
            placeholder={t('agency_placeholder')}
            autocomplete="false"
          />
        </div>

        <div className={styles['field-container']}>
          <Tooltip text={t('fps_tooltip')} position="top">
            <label>{t('fps')}</label>
          </Tooltip>

          <Controller
            name="fps"
            control={control}
            render={({ field }) => (
              <FpsSelect
                initialValue={defaultFpsValue}
                value={field.value!}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </form>

      <div className={styles['button-wrapper']}>
        <ElevatedButton
          key="submit"
          isLoading={isLoading}
          disabled={isLoading}
          onClick={handleSubmit(onHandleSubmit)}
          text="Save"
        />
      </div>
    </div>
  );
};
